import { useState } from "react"
import { Link, useLocation, useNavigate } from "react-router"
import { Eye, EyeOff } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { apiClient } from "@/lib/api"
import { trackEvent } from "@/lib/posthog"
import { AuthMethodDivider, GoogleAuthButton } from "@/components/auth/GoogleAuthButton"

export function SignUpPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const nextParam = params.get("next") || ""
  const nextPath = nextParam.startsWith("/") && !nextParam.startsWith("//") && nextParam !== "/sign-up"
    ? nextParam
    : "/"
  const [showPassword, setShowPassword] = useState(false)
  const [form, setForm] = useState({ firstName: "", lastName: "", companyName: "", email: "", password: "", confirmPassword: "" })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (form.password !== form.confirmPassword) {
      setError("Passwords do not match.")
      return
    }
    setError("")
    setLoading(true)

    try {
      const fullName = `${form.firstName.trim()} ${form.lastName.trim()}`.trim()
      // Read chatbot visitor ID cookie for cross-session attribution
      const chatVisitorId = document.cookie.match(/(?:^| )sf_vid=([^;]+)/)?.[1] || undefined
      const result = await apiClient.register(fullName, form.email, form.password, chatVisitorId)
      if (!result.ok) {
        setError(result.error || "Failed to create account.")
        return
      }
      trackEvent("signup_completed", { method: "email", has_company_name: Boolean(form.companyName.trim()) })
      navigate("/check-inbox", { state: { email: form.email } })
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Something went wrong. Please try again."
      trackEvent("signup_failed", { error_code: msg, method: "email" })
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-[400px]">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block font-display text-2xl font-bold tracking-tight text-foreground">
            Stayframe
          </Link>
          <p className="text-sm text-muted-foreground mt-2">
            Start your free 14-day trial
          </p>
        </div>

        {/* Card */}
        <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
          <GoogleAuthButton nextPath={nextPath} />
          <AuthMethodDivider />

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <p className="text-sm text-destructive text-center">{error}</p>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="firstName">First name</Label>
                <Input
                  id="firstName"
                  placeholder="Jane"
                  required
                  maxLength={50}
                  value={form.firstName}
                  onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last name</Label>
                <Input
                  id="lastName"
                  placeholder="Host"
                  required
                  maxLength={50}
                  value={form.lastName}
                  onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="companyName">Company name <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Input
                id="companyName"
                placeholder="Sunshine Rentals"
                maxLength={100}
                value={form.companyName}
                onChange={(e) => setForm({ ...form, companyName: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="jane@example.com"
                required
                maxLength={255}
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Min. 10 characters"
                  required
                  minLength={10}
                  maxLength={128}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 appearance-none border-none bg-transparent p-0 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm password</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showPassword ? "text" : "password"}
                  placeholder="Repeat your password"
                  required
                  minLength={10}
                  maxLength={128}
                  value={form.confirmPassword}
                  onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 appearance-none border-none bg-transparent p-0 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Creating account\u2026" : "Create account"}
            </Button>
          </form>

          <p className="text-sm text-center text-muted-foreground mt-5">
            Already have an account?{" "}
            <Link to={nextPath && nextPath !== "/" ? `/login?next=${encodeURIComponent(nextPath)}` : "/login"} className="text-primary font-medium hover:underline underline-offset-4">
              Sign in
            </Link>
          </p>
        </div>

        <p className="text-xs text-center text-muted-foreground/60 mt-6">
          By creating an account you agree to our{" "}
          <a href="https://www.stayframe.ai/terms" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-muted-foreground">Terms</a>
          {" "}and{" "}
          <a href="https://www.stayframe.ai/privacy" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-muted-foreground">Privacy Policy</a>.
        </p>
      </div>
    </div>
  )
}
